import type {
  DefaultSharedCoreModuleContext,
  LangiumCoreServices,
  LangiumSharedCoreServices,
  Module,
  PartialLangiumCoreServices,
} from 'langium';
import {
  EmptyFileSystem,
  createDefaultCoreModule,
  createDefaultSharedCoreModule,
  inject,
} from 'langium';

import { TreeViewValueConverter } from './valueConverter.js';
import {
  MermaidGeneratedSharedModule,
  TreeViewGrammarGeneratedModule as TreeViewGeneratedModule,
} from '../generated/module.js';
import { TreeViewTokenBuilder } from './tokenBuilder.js';

/**
 * Declaration of `TreeView` services.
 */
interface TreeViewAddedServices {
  parser: {
    TokenBuilder: TreeViewTokenBuilder;
    ValueConverter: TreeViewValueConverter;
  };
}

/**
 * Union of Langium default services and `TreeView` services.
 */
export type TreeViewServices = LangiumCoreServices & TreeViewAddedServices;

/**
 * Dependency injection module that overrides Langium default services and
 * contributes the declared `TreeView` services.
 */
export const TreeViewModule: Module<
  TreeViewServices,
  PartialLangiumCoreServices & TreeViewAddedServices
> = {
  parser: {
    TokenBuilder: () => new TreeViewTokenBuilder(),
    ValueConverter: () => new TreeViewValueConverter(),
  },
};

/**
 * Create the full set of services required by Langium.
 *
 * First inject the shared services by merging two modules:
 *  - Langium default shared services
 *  - Services generated by langium-cli
 *
 * Then inject the language-specific services by merging three modules:
 *  - Langium default language-specific services
 *  - Services generated by langium-cli
 *  - Services specified in this file
 * @param context - Optional module context with the LSP connection
 * @returns An object wrapping the shared services and the language-specific services
 */
export function createTreeViewServices(context: DefaultSharedCoreModuleContext = EmptyFileSystem): {
  shared: LangiumSharedCoreServices;
  TreeView: TreeViewServices;
} {
  const shared: LangiumSharedCoreServices = inject(
    createDefaultSharedCoreModule(context),
    MermaidGeneratedSharedModule
  );
  const TreeView: TreeViewServices = inject(
    createDefaultCoreModule({ shared }),
    TreeViewGeneratedModule,
    TreeViewModule
  );
  shared.ServiceRegistry.register(TreeView);
  return { shared, TreeView };
}
